/**
 * @author: lv.chen
 * @date: 2025/3/6
 * @desc: 新特性强提示Bubble组件，用于有新功能上线时，强制提醒交互
 */
import React from 'react';
import classnames from 'classnames';
import { BubbleProps } from 'tea-component';
import { Bubble } from '@home/components/TeaComponents';
import NewFeatureTipsContent from '@gp/components/NewFeatureTipsContent';
import useTipsView from '@gp/hooks/useTipsView';

import type { IBaseProps } from '@home/types/common';

export interface INewFeatureTipsBubbleCoreProps {
  // 新特性强提醒key（对应数据库中记录该强提醒的key）
  newFeatureTipsKey: string;
  // 新特性强提醒是否已准备好展示
  newFeatureTipsReadyShow?: boolean;
  // 新特性强提醒的提示内容
  newFeatureTipsContent?: string | React.ReactElement | null;
  // 普通提醒（鼠标hover的时候的提醒，强提醒存在时，普通提醒失效）
  commonTipsContent?: string | React.ReactElement | null;
}

export interface INewFeatureTipsBubbleProps
  extends IBaseProps,
  Omit<BubbleProps, 'visible' | 'content' | 'children'>,
  INewFeatureTipsBubbleCoreProps {}

const NewFeatureTipsBubble: React.FC<INewFeatureTipsBubbleProps> = (props) => {
  const {
    style,
    className,
    overlayStyle,
    dark = true,
    placement = 'top',
    flipped = false,
    escapeWithReference = true,
    arrowPointAtCenter = true,
    children,
    newFeatureTipsKey = '',
    newFeatureTipsContent,
    newFeatureTipsReadyShow = true,
    commonTipsContent,
    ...restProps
  } = props;

  const { maxWidth = 450 } = style || {};

  const { zIndex: overlayZIndex = 1 } = overlayStyle || {};

  const {
    // 是否已查看新特性强提醒
    isTipsView,
    // 关闭新特性强提醒气泡（代表确认已查看新特性强提醒）
    handleViewedTips,
  } = useTipsView(newFeatureTipsKey);

  return (
    <Bubble
      {...restProps}
      style={{ ...style, maxWidth }}
      overlayStyle={{ ...overlayStyle, zIndex: overlayZIndex }}
      className={classnames('dbg-bubble-max-width dbg-new-feature-tips-bubble', className)}
      dark={dark}
      placement={placement}
      flipped={flipped}
      escapeWithReference={escapeWithReference}
      visible={!isTipsView ? newFeatureTipsReadyShow : undefined}
      arrowPointAtCenter={arrowPointAtCenter}
      content={
        isTipsView ? (
          commonTipsContent
        ) : newFeatureTipsReadyShow ? (
          <NewFeatureTipsContent description={newFeatureTipsContent} onClose={handleViewedTips} />
        ) : undefined
      }
    >
      {children}
    </Bubble>
  );
};

export default NewFeatureTipsBubble;
